import { isPending } from '@fleetcore/shared'
import { ChevronRight, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { DashboardTile } from './api-types'
import { localisedReasonKey } from './format'
import { worstDriversOf } from './tiles'

/**
 * The five worst-scored drivers this week — §8.8's one list tile.
 *
 * ─── AN EMPTY LIST IS NOT "NO BAD DRIVERS" ───────────────────────────────────
 *
 * The API sends `PendingMetric('insufficient_history')` rather than an empty array when
 * nobody cleared the scoring distance floor, and this component renders that sentence
 * instead of a tidy empty state. A clean leaderboard is a commendation, and handing one
 * to a fleet that simply has not driven enough to be scored is the most flattering lie
 * this page could tell.
 *
 * ─── AND FEWER THAN FIVE IS A LEGITIMATE ANSWER ──────────────────────────────
 *
 * `ScoringService.leaderboard` decides who is RANKABLE. It will return three names when
 * only three drivers cleared the floor, and padding the list from the next-worst
 * unrankable drivers would put somebody on a coaching list on the strength of 12 km.
 */
export function WorstDriversTile({
  tile,
  weekLabel,
  isLoading = false,
  className,
}: {
  readonly tile: DashboardTile
  readonly weekLabel: string | null
  readonly isLoading?: boolean
  readonly className?: string
}) {
  const { t, i18n } = useTranslation()
  const format = useFormatters()

  const drivers = worstDriversOf(tile)
  const pending = isPending(tile.metric)
  const reasonKey = pending && tile.metric.reasonKey ? localisedReasonKey(tile.metric.reasonKey) : null // prettier-ignore
  const reasonText =
    reasonKey !== null && i18n.exists(reasonKey)
      ? t(reasonKey)
      : t('common:metric.insufficientHistory')

  return (
    <section
      aria-busy={isLoading || undefined}
      className={cn(
        'flex min-w-0 flex-col rounded-lg border border-border bg-surface shadow-card',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2 px-4 pb-2 pt-4">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium uppercase tracking-wide text-foreground-muted">
            {t('dashboard:tiles.driversWorstWeek.label')}
          </p>
          {weekLabel === null ? null : (
            <p className="mt-0.5 truncate text-xs text-foreground-subtle">{weekLabel}</p>
          )}
        </div>
        <Users aria-hidden className="h-4 w-4 shrink-0 text-foreground-subtle" />
      </div>

      {pending || drivers.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-foreground-subtle">{reasonText}</p>
      ) : (
        <ul className="px-2 pb-2">
          {drivers.map((driver) => (
            <li key={driver.driverId}>
              <Link
                to={driver.route === '' ? tile.route : driver.route}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-md px-2 py-1.5',
                  'transition-colors hover:bg-surface-hover',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {driver.rank === null ? null : (
                    <span className="w-4 shrink-0 text-xs tabular-nums text-foreground-subtle">
                      {format.integer(driver.rank)}
                    </span>
                  )}
                  <span className="truncate text-sm text-foreground">{driver.driverName}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {/* A rankable driver always has a score; the guard is for a row that
                      arrived without one, which renders as unknown rather than as 0 —
                      a driver displayed at zero is a driver about to be disciplined. */}
                  <span className="text-sm font-semibold tabular-nums text-foreground">
                    {driver.score === null
                      ? t('common:metric.unknown')
                      : format.integer(driver.score)}
                  </span>
                  <ChevronRight aria-hidden className="h-4 w-4 text-foreground-subtle" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Link
        to={tile.route}
        className={cn(
          'mt-auto border-t border-border px-4 py-2 text-xs font-medium text-accent-text',
          'transition-colors hover:bg-surface-hover',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        )}
      >
        {t('dashboard:tiles.driversWorstWeek.openLeaderboard')}
      </Link>
    </section>
  )
}
